import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authSelector } from '../../features/auth/slice';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Search, Loader2, Download, Music2, CheckCircle2, ChevronDown, Copy, X, Instagram, AlertCircle, TrendingUp, MapPin, Sparkles, RefreshCw, Trash2 } from 'lucide-react';

function buildCaption(album) {
  const artist   = (album.artistName || '').trim();
  const name     = (album.nameKr    || '').trim();
  const artistTag = artist.replace(/\s+/g, '');
  const albumTag  = name.replace(/\s+/g, '');
  return [
    `🎵 NEW MUSIC`,
    ``,
    `${artist} | ${name}`,
    ``,
    `새 음악이 공개되었습니다.`,
    `지금 p!nk에서 만나보세요 🎧`,
    ``,
    `▶ Link in Bio`,
    ``,
    `#${artistTag} #${albumTag} #신보 #핑크 #pink #인디음악 #Kindie`,
  ].join('\n');
}

const FALLBACK_IMG = 'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
}

// ── 업로드 모달 ───────────────────────────────────────────────────────────
function UploadModal({ album, onClose, onUploaded }) {
  const [step, setStep]         = useState('loading');
  const [cards, setCards]       = useState(null);
  const [caption, setCaption]   = useState('');
  const [copied, setCopied]     = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`/api/card-news/preview/${album.id}`, {}, { timeout: 300000 });
        if (cancelled) return;
        setCards(res.data);
        setCaption(buildCaption(album));
        setStep('preview');
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e.response?.data?.error || '카드 생성에 실패했습니다.');
          setStep('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [album]);

  const handleUpload = async () => {
    setStep('uploading');
    try {
      const res = await axios.post('/api/card-news/instagram-upload', {
        videoUrl:  cards.card1Url,
        imageUrls: [cards.card2Url, cards.card3Url],
        caption,
      });
      setResult(res.data);
      setStep('done');
      onUploaded && onUploaded(album.id);
    } catch (e) {
      setErrorMsg(e.response?.data?.error || '업로드에 실패했습니다.');
      setStep('error');
    }
  };

  const handleDownloadZip = async () => {
    try {
      const res = await axios.post(`/api/card-news/generate/${album.id}`, {}, {
        responseType: 'blob', timeout: 300000,
      });
      const url = window.URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href    = url;
      const safe = (album.nameKr || album.id).replace(/[^\w가-힣\s]/g,'').trim().replace(/\s+/g,'_');
      a.download = `cardnews_${safe}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('ZIP 생성 실패'); }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="relative w-full max-w-3xl bg-[#0f1823] border border-white/10 rounded-2xl shadow-2xl">
        {/* 헤더 */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0f1823] border-b border-white/8">
          <div>
            <h2 className="text-white font-bold text-base">{album.nameKr}</h2>
            <p className="text-slate-500 text-xs mt-0.5">{album.artistName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 카드 미리보기 */}
          <div>
            <p className="text-xs text-slate-500 mb-3 font-medium">카드 미리보기</p>
            {step === 'loading' && (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
                <Loader2 size={28} className="animate-spin text-sky-400" />
                <p className="text-sm">카드 생성 중... (1~2분 소요)</p>
              </div>
            )}
            {step !== 'loading' && cards && (
              <div className="grid grid-cols-3 gap-3">
                {[cards.card1Url, cards.card2Url, cards.card3Url].map((url, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-white/10 aspect-[4/5]">
                    <img src={url} alt={`카드 ${i+1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 캡션 편집 */}
          {(step === 'preview' || step === 'uploading' || step === 'done' || step === 'error') && cards && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-500 font-medium">SNS 캡션 (수정 가능)</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(caption); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                  {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  {copied ? '복사됨' : '복사'}
                </button>
              </div>
              <textarea
                value={caption}
                onChange={e => setCaption(e.target.value)}
                rows={10}
                disabled={step === 'uploading' || step === 'done'}
                className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-[12px] text-slate-300 leading-relaxed resize-none focus:outline-none focus:border-sky-500/30 disabled:opacity-60"
              />
            </div>
          )}

          {/* 에러 */}
          {step === 'error' && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">
              {errorMsg}
            </div>
          )}

          {/* 완료 */}
          {step === 'done' && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-1">
              <p className="text-emerald-400 text-sm font-semibold">업로드 완료!</p>
              <p className="text-slate-400 text-xs">Instagram 게시 완료{result?.fbPostId ? ' · Facebook 페이지 동시 게시 완료' : ' (Facebook 페이지 게시는 Instagram 연동 설정 확인 필요)'}</p>
            </div>
          )}

          {/* 버튼 영역 */}
          {(step === 'preview' || step === 'error') && (
            <div className="flex gap-3">
              <button
                onClick={handleDownloadZip}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
                <Download size={14} /> ZIP 다운로드
              </button>
              {step === 'preview' && (
                <button
                  onClick={handleUpload}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white transition-all">
                  <Instagram size={15} /> Instagram · Facebook 업로드
                </button>
              )}
            </div>
          )}

          {step === 'uploading' && (
            <div className="flex items-center justify-center gap-2 py-3 text-slate-400 text-sm">
              <Loader2 size={15} className="animate-spin" /> Instagram에 게시 중...
            </div>
          )}

          {step === 'done' && (
            <div className="flex gap-3">
              <button
                onClick={handleDownloadZip}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
                <Download size={14} /> ZIP 다운로드
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/15 text-white transition-colors">
                닫기
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── 뮤직트렌드 카드 미리보기 모달 ────────────────────────────────────────
function TrendCardModal({ trend, onClose, onUploaded }) {
  const alreadyUploaded = !!trend.igPostedAt;
  const [step, setStep]       = useState(alreadyUploaded ? 'loading' : 'loading');
  const [urls, setUrls]       = useState([]);
  const [caption, setCaption] = useState('');
  const [copied, setCopied]   = useState(false);
  const [errorMsg, setErr]    = useState('');
  const [result, setResult]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`/api/card-news/trend-preview/${trend.id}`, {}, { timeout: 300000 });
        if (cancelled) return;
        setUrls(res.data.urls || []);
        setCaption(res.data.caption || '');
        setStep('preview');
      } catch (e) {
        if (!cancelled) { setErr(e.response?.data?.error || '카드 생성 실패'); setStep('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [trend.id]);

  const handleUpload = async () => {
    setStep('uploading');
    try {
      const res = await axios.post(`/api/card-news/trend-upload/${trend.id}`, {}, { timeout: 300000 });
      setResult(res.data);
      setStep('done');
      onUploaded && onUploaded(trend.id);
    } catch (e) {
      setErr(e.response?.data?.error || '업로드 실패'); setStep('error');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="relative w-full max-w-4xl bg-[#0f1823] border border-white/10 rounded-2xl shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0f1823] border-b border-white/8">
          <div>
            <h2 className="text-white font-bold text-base line-clamp-1">{trend.title}</h2>
            <p className="text-slate-500 text-xs mt-0.5">{trend.category} · {trend.weekOf}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
              <Loader2 size={28} className="animate-spin text-pink-400" />
              <p className="text-sm">카드 생성 중... (1~2분 소요)</p>
            </div>
          )}
          {urls.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-3 font-medium">카드 미리보기 ({urls.length}장)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 overflow-x-auto">
                {urls.map((url, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-white/10 flex-shrink-0" style={{ aspectRatio: '4/5' }}>
                    <img src={url} alt={`카드 ${i+1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 캡션 */}
          {caption && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-500 font-medium">SNS 캡션 (수정 가능)</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(caption); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                  {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  {copied ? '복사됨' : '복사'}
                </button>
              </div>
              <textarea
                value={caption}
                onChange={e => setCaption(e.target.value)}
                rows={10}
                disabled={step === 'uploading' || step === 'done'}
                className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-[12px] text-slate-300 leading-relaxed resize-none focus:outline-none focus:border-pink-500/30 disabled:opacity-60"
              />
            </div>
          )}

          {step === 'error' && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">{errorMsg}</div>
          )}
          {step === 'done' && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-emerald-400 text-sm font-semibold">Instagram 업로드 완료!</p>
            </div>
          )}
          {step === 'preview' && (
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
                닫기
              </button>
              {alreadyUploaded ? (
                <div className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500/20 border border-emerald-500/30 text-emerald-400">
                  <CheckCircle2 size={15} /> 이미 업로드됨
                </div>
              ) : (
                <button
                  onClick={handleUpload}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white transition-all">
                  <Instagram size={15} /> Instagram 업로드
                </button>
              )}
            </div>
          )}
          {step === 'uploading' && (
            <div className="flex items-center justify-center gap-2 py-3 text-slate-400 text-sm">
              <Loader2 size={15} className="animate-spin" /> 업로드 중...
            </div>
          )}
          {step === 'done' && (
            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/15 text-white transition-colors">닫기</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── 핫플힙플 카드 모달 ────────────────────────────────────────────────────
function HotPlaceCardModal({ article, onClose, onUploaded }) {
  const alreadyUploaded = !!article.igPostedAt;
  const [step, setStep]       = useState('loading');
  const [urls, setUrls]       = useState([]);
  const [caption, setCaption] = useState('');
  const [copied, setCopied]   = useState(false);
  const [errorMsg, setErr]    = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`/api/card-news/hotplace-preview/${article.id}`, {}, { timeout: 300000 });
        if (cancelled) return;
        setUrls(res.data.urls || []);
        setCaption(res.data.caption || '');
        setStep('preview');
      } catch (e) {
        if (!cancelled) { setErr(e.response?.data?.error || '카드 생성 실패'); setStep('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [article.id]);

  const handleUpload = async () => {
    setStep('uploading');
    try {
      await axios.post(`/api/card-news/hotplace-upload/${article.id}`, {}, { timeout: 300000 });
      setStep('done');
      onUploaded && onUploaded(article.id);
    } catch (e) {
      setErr(e.response?.data?.error || '업로드 실패'); setStep('error');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="relative w-full max-w-4xl bg-[#0f1823] border border-white/10 rounded-2xl shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0f1823] border-b border-white/8">
          <div>
            <h2 className="text-white font-bold text-base line-clamp-1">{article.title}</h2>
            <p className="text-slate-500 text-xs mt-0.5">핫플힙플 · {article.createdAt ? new Date(article.createdAt).toLocaleDateString('ko-KR') : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
              <Loader2 size={28} className="animate-spin text-rose-400" />
              <p className="text-sm">카드 생성 중... (1~2분 소요)</p>
            </div>
          )}
          {urls.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-3 font-medium">카드 미리보기 ({urls.length}장)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {urls.map((url, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-white/10" style={{ aspectRatio: '4/5' }}>
                    <img src={url} alt={`카드 ${i+1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {caption && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-500 font-medium">SNS 캡션 (수정 가능)</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(caption); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                  {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  {copied ? '복사됨' : '복사'}
                </button>
              </div>
              <textarea
                value={caption}
                onChange={e => setCaption(e.target.value)}
                rows={10}
                disabled={step === 'uploading' || step === 'done'}
                className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-[12px] text-slate-300 leading-relaxed resize-none focus:outline-none focus:border-rose-500/30 disabled:opacity-60"
              />
            </div>
          )}
          {step === 'error' && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">{errorMsg}</div>}
          {step === 'done' && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-emerald-400 text-sm font-semibold">Instagram 업로드 완료!</p>
            </div>
          )}
          {step === 'preview' && (
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">닫기</button>
              {alreadyUploaded ? (
                <div className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500/20 border border-emerald-500/30 text-emerald-400">
                  <CheckCircle2 size={15} /> 이미 업로드됨
                </div>
              ) : (
                <button onClick={handleUpload} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-400 hover:to-orange-400 text-white transition-all">
                  <Instagram size={15} /> Instagram 업로드
                </button>
              )}
            </div>
          )}
          {step === 'uploading' && (
            <div className="flex items-center justify-center gap-2 py-3 text-slate-400 text-sm">
              <Loader2 size={15} className="animate-spin" /> 업로드 중...
            </div>
          )}
          {step === 'done' && (
            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/15 text-white transition-colors">닫기</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── p!nk 홍보 카드 모달 ──────────────────────────────────────────────────
function PromoCardModal({ data, onClose, onDeleted }) {
  const { urls, caption: initCaption } = data;
  const [caption, setCaption]   = useState(initCaption || '');
  const [copied, setCopied]     = useState(false);
  const [step, setStep]         = useState('preview');
  const [errorMsg, setErr]      = useState('');

  const handleUpload = async () => {
    setStep('uploading');
    try {
      await axios.post('/api/card-news/promo-upload', { urls, caption }, { timeout: 120000 });
      setStep('done');
    } catch (e) {
      setErr(e.response?.data?.error || '업로드 실패'); setStep('error');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="relative w-full max-w-4xl bg-[#0f1823] border border-white/10 rounded-2xl shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0f1823] border-b border-white/8">
          <div>
            <h2 className="text-white font-bold text-base">p!nk 홍보 카드뉴스</h2>
            <p className="text-slate-500 text-xs mt-0.5">{urls.length}장 · Instagram 업로드 가능</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* 카드 미리보기 */}
          <div>
            <p className="text-xs text-slate-500 mb-3 font-medium">카드 미리보기 ({urls.length}장)</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {urls.map((url, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-white/10" style={{ aspectRatio: '4/5' }}>
                  <img src={url} alt={`카드 ${i+1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
          {/* 캡션 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-slate-500 font-medium">SNS 캡션 (수정 가능)</p>
              <button
                onClick={() => { navigator.clipboard.writeText(caption); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
                {copied ? '복사됨' : '복사'}
              </button>
            </div>
            <textarea
              value={caption}
              onChange={e => setCaption(e.target.value)}
              rows={10}
              disabled={step === 'uploading' || step === 'done'}
              className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-[12px] text-slate-300 leading-relaxed resize-none focus:outline-none focus:border-pink-500/30 disabled:opacity-60"
            />
          </div>
          {step === 'error' && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">{errorMsg}</div>}
          {step === 'done' && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-emerald-400 text-sm font-semibold">Instagram 업로드 완료!</p>
              <p className="text-slate-500 text-xs mt-1">다시 생성하면 새 버전을 만들 수 있어요.</p>
            </div>
          )}
          {(step === 'preview' || step === 'error') && (
            <div className="flex gap-3">
              <button
                onClick={() => { onDeleted(); onClose(); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/15 border border-red-500/20 transition-colors">
                <Trash2 size={14} /> 삭제
              </button>
              <button
                onClick={handleUpload}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white transition-all">
                <Instagram size={15} /> Instagram 업로드
              </button>
            </div>
          )}
          {step === 'uploading' && (
            <div className="flex items-center justify-center gap-2 py-3 text-slate-400 text-sm">
              <Loader2 size={15} className="animate-spin" /> 업로드 중...
            </div>
          )}
          {step === 'done' && (
            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/15 text-white transition-colors">닫기</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────
export default function AdminCardNewsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();
  useEffect(() => {
    if (me && me.superAdminYn !== 'Y') navigate(-1);
  }, [me, navigate]);

  const [search, setSearch]         = useState('');
  const [albums, setAlbums]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [hasMore, setHasMore]       = useState(false);
  const [offset, setOffset]         = useState(0);
  const [modal, setModal]           = useState(null);
  const [batch, setBatch]           = useState({ isRunning: false, total: 0, processed: 0 });
  const [trends, setTrends]               = useState([]);
  const [trendModal, setTrendModal]       = useState(null);
  const [hotPlaces, setHotPlaces]         = useState([]);
  const [hotPlaceModal, setHotPlaceModal] = useState(null);
  const [promoData, setPromoData]         = useState(null);   // { urls, caption }
  const [promoModal, setPromoModal]       = useState(false);
  const [promoLoading, setPromoLoading]   = useState(false);
  const [promoError, setPromoError]       = useState('');
  const searchTimer = useRef(null);
  const pollTimer   = useRef(null);
  const LIMIT = 10;

  const fetchAlbums = useCallback(async (q, off) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/card-news/albums', { params: { search: q, offset: off, limit: LIMIT } });
      const items = res.data || [];
      if (off === 0) setAlbums(items); else setAlbums(prev => [...prev, ...items]);
      setHasMore(items.length === LIMIT);
      setOffset(off + items.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 배치 상태 폴링
  const pollBatchStatus = useCallback(async () => {
    try {
      const res = await axios.get('/api/card-news/batch-status');
      const state = res.data;
      setBatch(state);
      if (state.isRunning) {
        // 배치 실행 중 → 앨범 목록도 갱신 (업로드 상태 반영)
        const albumRes = await axios.get('/api/card-news/albums', { params: { search, offset: 0, limit: offset || LIMIT } });
        setAlbums(albumRes.data || []);
        pollTimer.current = setTimeout(pollBatchStatus, 3000);
      } else {
        // 배치 종료 → 최종 목록 한번 더 갱신
        fetchAlbums(search, 0);
      }
    } catch {}
  }, [search, offset, fetchAlbums]);

  useEffect(() => {
    fetchAlbums('', 0);
    // 이번 주 뮤직트렌드 로드
    axios.get('/public/music-trends?limit=3').then(res => {
      setTrends((res.data.items || []).slice(0, 3));
    }).catch(() => {});
    // 핫플힙플 최신 기사 3개
    axios.get('/api/genie-magazine?limit=3').then(res => {
      setHotPlaces((res.data.items || res.data || []).slice(0, 3));
    }).catch(() => {});
    // 페이지 진입 시 미업로드 앨범 자동 배치 시작
    axios.post('/api/card-news/batch-upload').then(res => {
      if (res.data.isRunning) {
        setBatch({ isRunning: true, total: res.data.total || 0, processed: 0 });
        pollTimer.current = setTimeout(pollBatchStatus, 3000);
      }
    }).catch(() => {});
  }, [fetchAlbums]);

  // 폴링 클린업
  useEffect(() => () => clearTimeout(pollTimer.current), []);

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setOffset(0); fetchAlbums(q, 0); }, 400);
  };

  const handleUploaded = (albumId) => {
    setAlbums(prev => prev.map(a => a.id === albumId ? { ...a, uploadStatus: 'uploaded' } : a));
  };
  const handleTrendUploaded = (id) => {
    setTrends(prev => prev.map(t => t.id === id ? { ...t, igPostedAt: new Date().toISOString() } : t));
  };
  const handleHotPlaceUploaded = (id) => {
    setHotPlaces(prev => prev.map(a => a.id === id ? { ...a, igPostedAt: new Date().toISOString() } : a));
  };

  const handlePromoGenerate = async () => {
    setPromoLoading(true);
    setPromoError('');
    setPromoData(null);
    try {
      const res = await axios.post('/api/card-news/promo-generate', {}, { timeout: 300000 });
      setPromoData(res.data);
      setPromoModal(true);
    } catch (e) {
      setPromoError(e.response?.data?.error || '카드 생성에 실패했습니다.');
    } finally {
      setPromoLoading(false);
    }
  };

  const uploadedCount = albums.filter(a => a.uploadStatus === 'uploaded').length;
  const failedCount   = albums.filter(a => a.uploadStatus === 'failed').length;

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <h1 className="text-xl font-bold text-white">카드뉴스 관리</h1>

      {/* 앨범 카드뉴스 섹션 */}
      <div>
        <h2 className="text-base font-bold text-white flex items-center gap-2">
          <Music2 size={16} className="text-sky-400" /> 앨범 카드뉴스
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          앨범을 클릭하면 카드 3장을 미리보고 Instagram · Facebook에 게시할 수 있습니다. (매일 15:00 KST 자동 업로드)
        </p>
      </div>

      {/* 배치 진행 바 */}
      {batch.isRunning && batch.total > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>인스타그램 자동 업로드 진행 중...</span>
            <span>{batch.processed} / {batch.total}</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
              style={{ width: `${batch.total ? (batch.processed / batch.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* 상태 요약 */}
      {(uploadedCount > 0 || failedCount > 0) && (
        <div className="flex gap-3">
          {uploadedCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-1.5">
              <CheckCircle2 size={12} /> {uploadedCount}개 업로드 완료
            </div>
          )}
          {failedCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-1.5">
              <AlertCircle size={12} /> {failedCount}개 실패 (다음 배치에서 재시도)
            </div>
          )}
        </div>
      )}

      {/* 검색 */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={handleSearch}
          placeholder="앨범명 / 아티스트 검색..."
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50"
        />
      </div>

      {/* 앨범 그리드 */}
      {albums.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500 gap-3">
          <Music2 size={36} />
          <p>검색 결과가 없습니다.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {albums.map((album) => {
            const isUploaded = album.uploadStatus === 'uploaded';
            const isFailed   = album.uploadStatus === 'failed';
            const isActive   = album.id === batch.currentAlbumId;
            return (
              <div
                key={album.id}
                onClick={() => !isUploaded && setModal(album)}
                className={`relative rounded-xl overflow-hidden border transition-all group
                  ${isUploaded
                    ? 'border-emerald-500/30 opacity-50 cursor-default'
                    : isFailed
                    ? 'border-red-500/30 cursor-pointer hover:border-red-400/50'
                    : isActive
                    ? 'border-purple-500/50 ring-1 ring-purple-500/30 cursor-default'
                    : 'border-white/5 hover:border-sky-500/50 hover:ring-1 hover:ring-sky-500/30 cursor-pointer'
                  }`}>
                <div className="aspect-square">
                  <img
                    src={album.image || FALLBACK_IMG}
                    alt={album.nameKr}
                    className={`w-full h-full object-cover transition-transform duration-300 ${!isUploaded && !isActive ? 'group-hover:scale-105' : ''}`}
                  />
                </div>
                <div className="p-2.5 bg-[#182334]">
                  <p className="text-white text-xs font-semibold truncate">{album.nameKr}</p>
                  <p className="text-slate-500 text-[11px] truncate mt-0.5">{album.artistName}</p>
                  <p className="text-slate-600 text-[11px] mt-0.5">{formatDate(album.releaseDateTime)} · {album.songCount}곡</p>
                </div>
                {/* 오버레이 */}
                {isUploaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold bg-black/60 px-2.5 py-1 rounded-full">
                      <CheckCircle2 size={11} /> 업로드됨
                    </span>
                  </div>
                )}
                {isFailed && (
                  <div className="absolute top-2 right-2">
                    <span className="flex items-center gap-1 text-red-400 text-[10px] font-semibold bg-black/70 px-2 py-0.5 rounded-full">
                      <AlertCircle size={9} /> 실패
                    </span>
                  </div>
                )}
                {isActive && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <span className="flex items-center gap-1.5 text-white text-xs font-semibold">
                      <Loader2 size={12} className="animate-spin" /> 업로드 중
                    </span>
                  </div>
                )}
                {!isUploaded && !isActive && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs font-semibold flex items-center gap-1">
                      <Instagram size={12} /> 업로드
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 더보기 */}
      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={() => fetchAlbums(search, offset)}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 text-sm text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ChevronDown size={14} />}
            더보기
          </button>
        </div>
      )}

      {loading && albums.length === 0 && (
        <div className="flex justify-center py-12">
          <Loader2 size={22} className="animate-spin text-slate-500" />
        </div>
      )}

      {/* 뮤직트렌드 카드뉴스 섹션 */}
      {trends.length > 0 && (
        <div className="border-t border-white/5 pt-6 space-y-4">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <TrendingUp size={16} className="text-sky-400" /> 뮤직트렌드 카드뉴스
            </h2>
            <p className="text-xs text-slate-500 mt-1">이번 주 뮤직트렌드를 카드뉴스로 미리보기 및 Instagram 업로드합니다. (매주 금요일 12:00 KST 자동 업로드)</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {trends.map(trend => {
              const uploaded = !!trend.igPostedAt;
              return (
                <button
                  key={trend.id}
                  onClick={() => !uploaded && setTrendModal(trend)}
                  className={`flex items-start gap-3 p-3.5 border rounded-xl text-left transition-all group relative overflow-hidden
                    ${uploaded
                      ? 'border-emerald-500/30 opacity-50 cursor-default'
                      : 'bg-white/5 hover:bg-white/8 border-white/10 hover:border-pink-500/40'}`}>
                  <img src={trend.coverImage} alt={trend.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0 group-hover:scale-105 transition-transform" />
                  <div className="min-w-0">
                    <p className="text-white text-xs font-semibold leading-snug line-clamp-2">{trend.title}</p>
                    <p className="text-pink-400 text-[10px] mt-1">{trend.category}</p>
                    <p className="text-slate-600 text-[10px]">{trend.weekOf}</p>
                  </div>
                  {uploaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold bg-black/60 px-2.5 py-1 rounded-full">
                        <CheckCircle2 size={11} /> 업로드됨
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 핫플힙플 카드뉴스 섹션 */}
      {hotPlaces.length > 0 && (
        <div className="border-t border-white/5 pt-6 space-y-4">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <MapPin size={16} className="text-rose-400" /> 핫플힙플 카드뉴스
            </h2>
            <p className="text-xs text-slate-500 mt-1">최신 핫플힙플 기사를 카드뉴스로 미리보기 및 Instagram 업로드합니다. (새 기사 수집 시 자동 업로드)</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {hotPlaces.map(article => {
              const uploaded = !!article.igPostedAt;
              return (
                <button
                  key={article.id}
                  onClick={() => !uploaded && setHotPlaceModal(article)}
                  className={`flex items-start gap-3 p-3.5 border rounded-xl text-left transition-all group relative overflow-hidden
                    ${uploaded
                      ? 'border-emerald-500/30 opacity-50 cursor-default'
                      : 'bg-white/5 hover:bg-white/8 border-white/10 hover:border-rose-500/40'}`}>
                  {article.thumbnail && (
                    <img src={article.thumbnail} alt={article.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0 group-hover:scale-105 transition-transform" />
                  )}
                  <div className="min-w-0">
                    <p className="text-white text-xs font-semibold leading-snug line-clamp-2">{article.title}</p>
                    <p className="text-rose-400 text-[10px] mt-1">핫플힙플</p>
                    {!uploaded && <p className="text-slate-600 text-[10px] mt-0.5">미업로드</p>}
                  </div>
                  {uploaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold bg-black/60 px-2.5 py-1 rounded-full">
                        <CheckCircle2 size={11} /> 업로드됨
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* p!nk 홍보 카드뉴스 섹션 */}
      <div className="border-t border-white/5 pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Sparkles size={16} className="text-pink-400" /> p!nk 홍보 카드뉴스
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              p!nk 브랜드 홍보용 카드뉴스 8장을 AI로 생성합니다. 마음에 들면 업로드, 아니면 삭제 후 재생성 가능합니다.
            </p>
          </div>
          <button
            onClick={handlePromoGenerate}
            disabled={promoLoading}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-all">
            {promoLoading
              ? <><Loader2 size={14} className="animate-spin" /> 생성 중...</>
              : <><Sparkles size={14} /> 카드 생성</>}
          </button>
        </div>

        {promoError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">{promoError}</div>
        )}

        {promoLoading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-500">
            <Loader2 size={28} className="animate-spin text-pink-400" />
            <p className="text-sm">AI가 카피를 작성하고 카드를 렌더링 중... (1~2분 소요)</p>
          </div>
        )}

        {promoData && !promoLoading && (
          <div className="space-y-3">
            {/* 썸네일 미리보기 그리드 */}
            <div
              className="grid grid-cols-4 sm:grid-cols-8 gap-2 cursor-pointer group"
              onClick={() => setPromoModal(true)}>
              {promoData.urls.map((url, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-white/10 group-hover:border-pink-500/40 transition-all" style={{ aspectRatio: '4/5' }}>
                  <img src={url} alt={`홍보 카드 ${i+1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePromoGenerate}
                disabled={promoLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
                <RefreshCw size={12} /> 재생성
              </button>
              <button
                onClick={() => setPromoData(null)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/15 border border-red-500/20 transition-colors">
                <Trash2 size={12} /> 삭제
              </button>
              <button
                onClick={() => setPromoModal(true)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-pink-400 bg-pink-500/10 hover:bg-pink-500/15 border border-pink-500/20 transition-colors">
                <Instagram size={12} /> 미리보기 · 업로드
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="pb-12" />

      {/* 업로드 모달 */}
      {modal && <UploadModal album={modal} onClose={() => setModal(null)} onUploaded={handleUploaded} />}
      {trendModal && <TrendCardModal trend={trendModal} onClose={() => setTrendModal(null)} onUploaded={handleTrendUploaded} />}
      {hotPlaceModal && <HotPlaceCardModal article={hotPlaceModal} onClose={() => setHotPlaceModal(null)} onUploaded={handleHotPlaceUploaded} />}
      {promoModal && promoData && (
        <PromoCardModal
          data={promoData}
          onClose={() => setPromoModal(false)}
          onDeleted={() => setPromoData(null)}
        />
      )}
    </div>
  );
}
